import React from 'react';
import { useNavigate } from 'react-router-dom';

interface BasicBCPStepFlowProps {
  currentStep: 'restore-policy' | 'regulation' | 'initial-response' | 'procedure-manual';
  bcpId?: string;
  tenantId?: string | null;
  onStepNavigate?: (targetStep: string) => void | Promise<void>;
}

const BasicBCPStepFlow: React.FC<BasicBCPStepFlowProps> = ({
  currentStep,
  bcpId,
  tenantId,
  onStepNavigate,
}) => {
  const navigate = useNavigate();

  const steps = [
    { id: 'restore-policy', label: '復旧方針書' },
    { id: 'regulation', label: 'BCP規程' },
    { id: 'initial-response', label: '初動対応表' },
    { id: 'procedure-manual', label: '手順書' },
  ] as const;

  const handleStepClick = async (stepId: string) => {
    if (stepId === currentStep || !bcpId || bcpId === 'new') {
      return;
    }

    // If onStepNavigate is provided, delegate all navigation logic to the parent
    if (onStepNavigate) {
      const result = onStepNavigate(stepId);
      if (result instanceof Promise) {
        await result;
      }
      return;
    }

    // Fallback: simple navigation without state
    let targetUrl = `/bcp/${bcpId}/${stepId}`;
    if (tenantId) {
      targetUrl += `?tenantId=${tenantId}`;
    }

    navigate(targetUrl);
  };

  return (
    <div
      style={{
        backgroundColor: 'white',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        padding: '24px 25px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '48px',
          width: '100%',
        }}
      >
        {steps.map((step, index) => {
          const isActive = step.id === currentStep;
          const isLastStep = index === steps.length - 1;
          const isClickable = bcpId && bcpId !== 'new';

          return (
            <React.Fragment key={step.id}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ paddingLeft: '8px' }}>
                  <div
                    style={{
                      fontFamily: "'Noto Sans JP', sans-serif",
                      fontWeight: 500,
                      fontSize: '16px',
                      lineHeight: '24px',
                      color: isActive ? '#0d357f' : '#6b7280',
                      cursor: isClickable ? 'pointer' : 'default',
                      whiteSpace: 'nowrap',
                    }}
                    onClick={() => handleStepClick(step.id)}
                    role={isClickable ? 'button' : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    onKeyDown={(e) => {
                      if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        handleStepClick(step.id);
                      }
                    }}
                  >
                    {step.label}
                  </div>
                </div>
              </div>

              {!isLastStep && (
                <div
                  style={{
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    flex: '1 0 0',
                    minHeight: '1px',
                    minWidth: '1px',
                  }}
                >
                  <div
                    style={{
                      backgroundColor: '#d1d5db',
                      height: '1px',
                      width: '100%',
                    }}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default BasicBCPStepFlow;
